import styled from '@emotion/styled'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { DownOutlined, PictureOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import type {
  ConferenceDraft,
  DivisionDraft,
  WizardFormValues,
} from '@sportninja/common/features/competition/wizard'
import ConferenceRow from '../components/ConferenceRow'
import DivisionRow from '../components/DivisionRow'
import InlineCreateRow from '../components/InlineCreateRow'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'

type Props = { competitionName: string }

const makeClientId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `c-${Math.random().toString(36).slice(2, 11)}`

const Step4ConferencesSubStep = ({ competitionName }: Props) => {
  const { values, setFieldValue } = useFormikContext<WizardFormValues>()
  const conferences = values.conferences
  const sensors = useSensors(useSensor(PointerSensor))

  const handleConfDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = conferences.findIndex((c) => c.clientId === active.id)
    const newIndex = conferences.findIndex((c) => c.clientId === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }
    setFieldValue('conferences', arrayMove(conferences, oldIndex, newIndex))
  }

  const handleDivisionDragEnd = (confId: string) => (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) {
      return
    }
    const conf = conferences.find((c) => c.clientId === confId)
    if (!conf) {
      return
    }
    const oldIndex = conf.divisions.findIndex((d) => d.clientId === active.id)
    const newIndex = conf.divisions.findIndex((d) => d.clientId === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }
    setFieldValue(
      'conferences',
      conferences.map((c) =>
        c.clientId === confId
          ? { ...c, divisions: arrayMove(c.divisions, oldIndex, newIndex) }
          : c
      )
    )
  }

  const addConference = (name: string) => {
    const next: ConferenceDraft = {
      clientId: makeClientId(),
      name,
      order: conferences.length,
      divisions: [],
      isExpanded: true,
    }
    setFieldValue('conferences', [...conferences, next])
  }

  const renameConference = (clientId: string, name: string) => {
    setFieldValue(
      'conferences',
      conferences.map((c) => (c.clientId === clientId ? { ...c, name } : c))
    )
  }

  const deleteConference = (clientId: string) => {
    setFieldValue(
      'conferences',
      conferences.filter((c) => c.clientId !== clientId)
    )
  }

  const toggleExpand = (clientId: string) => {
    setFieldValue(
      'conferences',
      conferences.map((c) =>
        c.clientId === clientId ? { ...c, isExpanded: !c.isExpanded } : c
      )
    )
  }

  const addDivisionTo = (confId: string, name: string) => {
    const division: DivisionDraft = {
      clientId: makeClientId(),
      name,
      order: 0,
    }
    setFieldValue(
      'conferences',
      conferences.map((c) =>
        c.clientId === confId
          ? {
              ...c,
              divisions: [
                ...c.divisions,
                { ...division, order: c.divisions.length },
              ],
            }
          : c
      )
    )
  }

  const renameDivision = (confId: string, divId: string, name: string) => {
    setFieldValue(
      'conferences',
      conferences.map((c) =>
        c.clientId === confId
          ? {
              ...c,
              divisions: c.divisions.map((d) =>
                d.clientId === divId ? { ...d, name } : d
              ),
            }
          : c
      )
    )
  }

  const deleteDivision = (confId: string, divId: string) => {
    setFieldValue(
      'conferences',
      conferences.map((c) =>
        c.clientId === confId
          ? { ...c, divisions: c.divisions.filter((d) => d.clientId !== divId) }
          : c
      )
    )
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Create Conferences & Divisions
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Set up team groupings for the {competitionWord({ capitalize: false })}{' '}
          tables.
        </Text>
      </Header>
      <Tree>
        <CompetitionRow>
          <ChevronWrap>
            <DownOutlined />
          </ChevronWrap>
          <CompetitionIcon>
            <PictureOutlined />
          </CompetitionIcon>
          <CompetitionText>
            <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
              {competitionName || competitionWord()}
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
              {competitionWord()}
            </Text>
          </CompetitionText>
        </CompetitionRow>
        <Children>
          <DndContext sensors={sensors} onDragEnd={handleConfDragEnd}>
            <SortableContext
              items={conferences.map((c) => c.clientId)}
              strategy={verticalListSortingStrategy}
            >
              {conferences.map((c) => (
                <ChildItem key={c.clientId}>
                  <ConferenceRow
                    clientId={c.clientId}
                    name={c.name}
                    isExpanded={c.isExpanded}
                    onToggleExpand={() => toggleExpand(c.clientId)}
                    onRename={(name) => renameConference(c.clientId, name)}
                    onDelete={() => deleteConference(c.clientId)}
                  >
                    <DndContext
                      sensors={sensors}
                      onDragEnd={handleDivisionDragEnd(c.clientId)}
                    >
                      <SortableContext
                        items={c.divisions.map((d) => d.clientId)}
                        strategy={verticalListSortingStrategy}
                      >
                        {c.divisions.map((d) => (
                          <ChildItem key={d.clientId}>
                            <DivisionRow
                              clientId={d.clientId}
                              name={d.name}
                              onRename={(name) =>
                                renameDivision(c.clientId, d.clientId, name)
                              }
                              onDelete={() =>
                                deleteDivision(c.clientId, d.clientId)
                              }
                            />
                          </ChildItem>
                        ))}
                      </SortableContext>
                    </DndContext>
                    <ChildItem $compact>
                      <InlineCreateRow
                        label='Add Division'
                        placeholder='Division name'
                        onCreate={(name) => addDivisionTo(c.clientId, name)}
                      />
                    </ChildItem>
                  </ConferenceRow>
                </ChildItem>
              ))}
            </SortableContext>
          </DndContext>
          <ChildItem $compact>
            <InlineCreateRow
              label='Add Conference'
              placeholder='Conference name'
              onCreate={addConference}
            />
          </ChildItem>
        </Children>
      </Tree>
    </Container>
  )
}

export default Step4ConferencesSubStep

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Tree = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
`

const CompetitionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${colors.SECONDARY_500};
  border-radius: 8px;
`

const ChevronWrap = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.NEUTRAL_50};
`

const CompetitionIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.PRIMARY};
  color: ${colors.BLACK};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`

const CompetitionText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const Children = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 0 36px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    height: calc(100% - 15px);
    width: 1px;
    background: ${colors.SECONDARY_300};
  }
`

const ChildItem = styled.div<{ $compact?: boolean }>`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -16px;
    top: ${(p) => (p.$compact ? '19px' : '26px')};
    width: 16px;
    height: 1px;
    background: ${colors.SECONDARY_300};
  }
`
